import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, CalendarClock, Pause, Play, XCircle, Mail,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = '/api';

// Drip Schedule — read-only view of every recruit-drip enrollment: next touch,
// 90-day projection grouped by week, one-click pause/resume/remove per row.

const STATUS_CLS = {
  active: 'text-hg-green border-hg-green/40',
  paused: 'text-amber-300 border-amber-400/40',
  dormant: 'text-hg-text-dim border-hg-border',
  stopped: 'text-hg-text-dim border-hg-border line-through',
};

const fmtDay = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

function weekStart(iso) {
  const d = new Date(iso);
  d.setDate(d.getDate() - d.getDay());        // Sunday
  return d.toISOString().slice(0, 10);
}

function Row({ row, actor, onChanged }) {
  const [busy, setBusy] = useState('');
  const act = async (kind) => {
    if (kind === 'remove' && !window.confirm(`Remove ${row.email} from the drip permanently? (Record kept; sequence stops.)`)) return;
    setBusy(kind);
    try {
      await axios.post(`${API}/recruit-drip/enrollments/${row.enrollment_id}/${kind}`, { actor });
      onChanged?.();
    } catch (e) { window.alert(e.response?.data?.detail || 'Action failed.'); }
    setBusy('');
  };
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-hg-surface border border-hg-border/60 rounded-sm flex-wrap"
         data-testid={`drip-row-${row.enrollment_id}`}>
      <div className="min-w-0 flex-1">
        <span className="text-[13px] font-sans text-hg-text">{row.first_name || row.email}</span>
        <span className="text-[11px] font-sans text-hg-text-dim"> · {row.email}</span>
        <span className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider ml-2">{row.tier === 'pulse_only' ? 'pulse-only' : 'full'}</span>
      </div>
      <span className={`text-[10px] font-sans uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_CLS[row.status] || STATUS_CLS.dormant}`}>{row.status}</span>
      <span className="text-[11px] font-sans text-hg-text-dim inline-flex items-center gap-1">
        <Mail size={10} />
        {row.next ? `${row.next.tag} · ${fmtDay(row.next.due_at)}` : (row.status === 'active' ? 'beyond horizon' : '—')}
      </span>
      <div className="flex items-center gap-1.5">
        {(row.status === 'active' || row.status === 'paused') && (
          <button onClick={() => act('pause')} disabled={!!busy}
            title={row.status === 'paused' ? 'Resume' : 'Pause'}
            className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-green hover:border-hg-green disabled:opacity-40">
            {busy === 'pause' ? <Loader2 size={12} className="animate-spin" /> :
              row.status === 'paused' ? <Play size={12} /> : <Pause size={12} />}
          </button>
        )}
        {row.status !== 'stopped' && (
          <button onClick={() => act('remove')} disabled={!!busy} title="Remove from drip"
            className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-red-400 hover:border-red-400 disabled:opacity-40">
            {busy === 'remove' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DripSchedule() {
  const { user } = useAuth();
  const actor = user?.email || 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/recruit-drip/schedule?horizon_days=90`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load the schedule.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  // flatten every upcoming touch for the 90-day list, grouped by week
  const touches = rows.flatMap((r) => (r.status === 'active' ? (r.upcoming || []).map((t) => ({
    ...t, email: r.email, first_name: r.first_name, enrollment_id: r.enrollment_id,
  })) : []));
  touches.sort((a, b) => String(a.due_at).localeCompare(String(b.due_at)));
  const byWeek = touches.reduce((acc, t) => {
    const wk = weekStart(t.due_at);
    (acc[wk] = acc[wk] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="drip-schedule-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">DRIP SCHEDULE</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            Every recruit-drip enrollment and exactly what they'll receive, when.
            {data && !data.live_gate && (
              <span className="text-amber-300"> RECRUIT_DRIP_LIVE is OFF — these are projections; nothing sends.</span>
            )}
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>}

      {data && (
        <>
          {/* enrollments */}
          <div className="space-y-1.5">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
              Enrollments ({rows.length})
            </div>
            {rows.map((r) => <Row key={r.enrollment_id} row={r} actor={actor} onChanged={load} />)}
          </div>

          {/* 90-day projection by week */}
          <div className="space-y-3">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim inline-flex items-center gap-1.5">
              <CalendarClock size={12} className="text-hg-green" /> Next 90 days ({touches.length} touches)
            </div>
            {Object.entries(byWeek).map(([wk, list]) => (
              <div key={wk} className="bg-hg-surface border border-hg-border rounded-sm p-3">
                <div className="text-[11px] font-sans text-hg-green mb-2">Week of {fmtDay(wk)}</div>
                <div className="space-y-1">
                  {list.map((t, i) => (
                    <div key={i} className="text-[12px] font-sans text-hg-text flex items-center gap-2 flex-wrap">
                      <span className="text-hg-text-dim w-14 flex-shrink-0">{fmtDay(t.due_at)}</span>
                      <span className="text-[10px] uppercase tracking-wider border border-hg-border rounded-sm px-1.5 py-0.5 text-hg-text-dim flex-shrink-0">{t.tag}</span>
                      <span className="min-w-0">{t.first_name || t.email}
                        <span className="text-hg-text-dim"> · “{t.subject}”</span></span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {touches.length === 0 && (
              <div className="text-[12px] font-sans text-hg-text-dim italic">No touches scheduled inside 90 days.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
